// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { TransactionHistoryGroup } from '../../types/wizard';

interface TransactionHistoryTableProps {
  history: TransactionHistoryGroup;
  onClear?: () => void;
}

function fmtCurrency(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `€${value.toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IE', { day: '2-digit', month: 'short', year: 'numeric' });
}

const TYPE_LABELS: Record<string, string> = {
  REGULAR_CONTRIBUTION: 'Regular contribution',
  SINGLE_CONTRIBUTION: 'Single contribution',
  SWITCH: 'Switch',
  BUY: 'Buy',
  SELL: 'Sell',
  CHARGE: 'Charge',
  OTHER: 'Other',
};

/**
 * Read-only table rendering the transaction entries extracted from the
 * Standard Life transactions PDF. Mounted on Step 7 above the existing
 * contribution fields when `transactionHistory.entries.length > 0`.
 */
export function TransactionHistoryTable({ history, onClear }: TransactionHistoryTableProps) {
  const entries = history.entries ?? [];

  return (
    <div className="rounded-button border border-[#E1E1E1] bg-white p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-black font-sans text-body-02 font-medium">
            Transaction history
          </div>
          <div className="text-grey-04 text-body-04">
            {fmtDate(history.periodFrom)} – {fmtDate(history.periodTo)}
            {history.sourceDocumentRef ? ` · Source: ${history.sourceDocumentRef}` : ''}
          </div>
        </div>
        {onClear && (
          <button
            type="button"
            onClick={onClear}
            className="text-purple-primary text-body-03 underline"
          >
            Remove
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-body-03 font-sans">
          <thead>
            <tr className="text-grey-04 border-b border-[#E1E1E1]">
              <th className="py-2 pr-4 font-medium">Date</th>
              <th className="py-2 pr-4 font-medium">Type</th>
              <th className="py-2 pr-4 font-medium">Description</th>
              <th className="py-2 pr-4 font-medium text-right">In</th>
              <th className="py-2 pl-4 font-medium text-right">Out</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (
              <tr>
                <td colSpan={5} className="py-3 text-grey-04 text-center">
                  No transactions parsed.
                </td>
              </tr>
            )}
            {entries.map((entry, idx) => (
              <tr key={idx} className="border-b border-grey-06 align-top">
                <td className="py-2 pr-4 whitespace-nowrap">{fmtDate(entry.processedDate)}</td>
                <td className="py-2 pr-4 whitespace-nowrap">{TYPE_LABELS[entry.type] ?? entry.type}</td>
                <td className="py-2 pr-4">
                  <div>{entry.description}</div>
                  {entry.legs.length > 0 && (
                    <ul className="mt-1 text-body-04 text-grey-04 list-disc pl-4">
                      {entry.legs.map((leg, i) => (
                        <li key={i}>
                          {leg.action.replace('_', ' ').toLowerCase()}{' '}
                          {leg.units !== undefined && `${leg.units} units `}
                          {leg.fundName && `of ${leg.fundName} `}
                          {leg.unitPriceCents !== undefined && `@ ${leg.unitPriceCents}c `}
                          {leg.amount !== undefined && `= ${fmtCurrency(leg.amount)}`}
                        </li>
                      ))}
                    </ul>
                  )}
                </td>
                <td className="py-2 pr-4 text-right whitespace-nowrap">
                  {entry.amountIn != null ? fmtCurrency(entry.amountIn) : ''}
                </td>
                <td className="py-2 pl-4 text-right whitespace-nowrap">
                  {entry.amountOut != null ? fmtCurrency(entry.amountOut) : ''}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="text-black font-medium border-t border-[#E1E1E1]">
              <td colSpan={3} className="py-2 pr-4">
                {fmtCurrency(history.openingValue)} → {fmtCurrency(history.closingValue)}
              </td>
              <td className="py-2 pr-4 text-right">{fmtCurrency(history.totalPaymentsIn)}</td>
              <td className="py-2 pl-4 text-right">{fmtCurrency(history.totalPaymentsOut)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
